import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { money, dateShort } from "@/lib/format";
import { useClientPrices, useSaveClientPrice } from "../api";
import { EmptyState } from "@/components/shared/EmptyState";
import { DeleteDialog } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type Currency = "EUR" | "USD";

function PriceForm({ clientId, currency, onDone }: { clientId: string; currency: Currency; onDone: () => void }) {
  const save = useSaveClientPrice(clientId);
  const { data: products } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => { const { data, error } = await supabase.from("products").select("id, sku, name, unit_price_eur, unit_price_usd").eq("is_active", true).order("name"); if (error) throw error; return data; },
  });
  const [productId, setProductId] = useState("");
  const [cur, setCur] = useState<Currency>(currency);
  const [mode, setMode] = useState<"price" | "discount">("discount");
  const [value, setValue] = useState("");
  const [validFrom, setValidFrom] = useState(new Date().toISOString().slice(0, 10));
  const [validTo, setValidTo] = useState("");
  const [notes, setNotes] = useState("");
  const product = products?.find((p) => p.id === productId);
  const base = product ? (cur === "EUR" ? product.unit_price_eur : product.unit_price_usd) : null;
  const n = Number(value);
  const preview = base !== null && base !== undefined && value !== "" ? (mode === "price" ? n : Number(base) * (1 - n / 100)) : null;

  function submit() {
    if (!productId || value === "" || Number.isNaN(n) || n < 0 || (mode === "discount" && n > 100)) return;
    save.mutate({
      product_id: productId, currency: cur,
      unit_price: mode === "price" ? n : null, discount_pct: mode === "discount" ? n : null,
      valid_from: validFrom, valid_to: validTo || null, notes: notes.trim() || null,
    }, { onSuccess: onDone });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="cp-product">Produit</Label>
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger id="cp-product"><SelectValue placeholder="Choisir un produit" /></SelectTrigger>
          <SelectContent>{products?.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="cp-currency">Devise</Label>
          <Select value={cur} onValueChange={(v) => setCur(v as Currency)}>
            <SelectTrigger id="cp-currency"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cp-mode">Type</Label>
          <Select value={mode} onValueChange={(v) => setMode(v as "price" | "discount")}>
            <SelectTrigger id="cp-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="discount">Remise (%)</SelectItem><SelectItem value="price">Prix unitaire fixé</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cp-value">{mode === "discount" ? "Remise %" : `Prix ${cur}`}</Label>
          <Input id="cp-value" type="number" step="0.01" min="0" max={mode === "discount" ? 100 : undefined} value={value} onChange={(e) => setValue(e.target.value)} />
        </div>
        <div className="space-y-1.5"><Label htmlFor="cp-from">Valable du</Label><Input id="cp-from" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} /></div>
        <div className="space-y-1.5"><Label htmlFor="cp-to">au (facultatif)</Label><Input id="cp-to" type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} /></div>
        <div className="space-y-1.5"><Label htmlFor="cp-notes">Note</Label><Input id="cp-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Négocié le…" /></div>
      </div>
      <div className="rounded-md bg-muted px-3 py-2 text-sm">
        Prix catalogue : <strong>{money(base, cur)}</strong>
        {preview !== null && <> → prix appliqué : <strong>{money(preview, cur)}</strong></>}
      </div>
      <div className="flex justify-end"><Button onClick={submit} disabled={save.isPending || !productId || value === ""}>Enregistrer</Button></div>
    </div>
  );
}

export function PricesTab({ clientId, currency }: { clientId: string; currency: Currency }) {
  const { data: prices, isLoading } = useClientPrices(clientId);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">Un tarif négocié remplace le prix catalogue pour ce client, sur sa période de validité. Le plus récent l'emporte.</p>
        <Button size="sm" onClick={() => setAdding(true)}><Plus className="mr-2 h-4 w-4" />Nouveau tarif</Button>
      </div>
      {isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !prices?.length ? (
        <EmptyState title="Aucun tarif négocié" description="Ce client paie le prix catalogue." />
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader><TableRow><TableHead>Produit</TableHead><TableHead>Devise</TableHead><TableHead className="text-right">Catalogue</TableHead><TableHead className="text-right">Négocié</TableHead><TableHead>Validité</TableHead><TableHead>Note</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {prices.map((p) => {
                const prod = p.products as { sku: string; name: string; unit_price_eur: number | null; unit_price_usd: number | null } | null;
                const base = prod ? (p.currency === "EUR" ? prod.unit_price_eur : prod.unit_price_usd) : null;
                const applied = p.unit_price !== null ? Number(p.unit_price) : base !== null && base !== undefined ? Number(base) * (1 - Number(p.discount_pct) / 100) : null;
                const active = p.valid_from <= today && (!p.valid_to || p.valid_to >= today);
                return (
                  <TableRow key={p.id} className={active ? "" : "opacity-60"}>
                    <TableCell><span className="font-mono text-xs">{prod?.sku}</span> {prod?.name}</TableCell>
                    <TableCell>{p.currency}</TableCell>
                    <TableCell className="text-right">{money(base, p.currency)}</TableCell>
                    <TableCell className="text-right font-medium">{money(applied, p.currency)}{p.discount_pct !== null && <span className="ml-1 text-xs text-muted-foreground">(−{Number(p.discount_pct)} %)</span>}</TableCell>
                    <TableCell className="text-sm">{p.valid_to ? `du ${dateShort(p.valid_from)} au ${dateShort(p.valid_to)}` : `depuis le ${dateShort(p.valid_from)}`}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{p.notes}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleting(p.id)} aria-label="Supprimer ce tarif"><Trash2 className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {deleting && <DeleteDialog entity="client_price" id={deleting} open onOpenChange={(o) => !o && setDeleting(null)} onDeleted={() => setDeleting(null)} />}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nouveau tarif négocié</DialogTitle><DialogDescription>Remise en pourcentage sur le catalogue, ou prix unitaire fixé.</DialogDescription></DialogHeader>
          <PriceForm clientId={clientId} currency={currency} onDone={() => setAdding(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
